import {announce} from '@github-ui/aria-live'
import {Dialog} from '@primer/react'
import {InlineMessage} from '@primer/react/experimental'
import {useEffect, useRef} from 'react'

export type UnstackDialogProps = {
  errorMessage: string
  isAutoMergeEnabled?: boolean
  /** Whether the stack's auto-merge state is still loading. Unstacking waits for it, so the copy is right. */
  isCheckingAutoMerge?: boolean
  isConfirmingUnstack: boolean
  isLoading: boolean
  onCancelUnstack: () => void
  onConfirmUnstack: () => void
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
  someLockedPulls?: boolean
}

export function UnstackDialog({
  returnFocusRef,
  onCancelUnstack,
  onConfirmUnstack,
  isAutoMergeEnabled,
  isCheckingAutoMerge = false,
  isConfirmingUnstack,
  isLoading,
  errorMessage,
  someLockedPulls,
}: UnstackDialogProps) {
  let unstackMessage = someLockedPulls
    ? 'Pull requests queued for merge or already merged will remain in the stack. For pull requests that are removed, base branches will not be retargeted and commit histories will remain the same.'
    : 'All pull requests will be removed from the stack. Their base branches will not be retargeted and their commit histories will remain the same.'
  if (isAutoMergeEnabled) {
    unstackMessage = `Auto-merge will be disabled for this stack. ${unstackMessage}`
  }

  if (!isConfirmingUnstack) {
    return null
  }

  const isUnstackDisabled = isLoading || isCheckingAutoMerge

  return (
    <Dialog
      title="Are you sure you want to unstack these pull requests?"
      width="large"
      returnFocusRef={returnFocusRef}
      onClose={onCancelUnstack}
      footerButtons={[
        {buttonType: 'default', content: 'Cancel', onClick: onCancelUnstack},
        {
          buttonType: 'danger',
          content: 'Unstack',
          onClick: onConfirmUnstack,
          // aria-disabled must be not set when false to avoid broken styling
          'aria-disabled': isUnstackDisabled ? 'true' : undefined,
          disabled: isUnstackDisabled,
          loading: isLoading,
        },
      ]}
    >
      <UnstackMessage message={unstackMessage} />
      {errorMessage && <UnstackError message={errorMessage} />}
      {/* Loading message accessible to screen readers only. See https://primer.style/components/button#button-loading-state */}
      <span className="sr-only" aria-live="polite">
        {isLoading
          ? 'Unstacking pull requests in progress.'
          : isCheckingAutoMerge
            ? 'Checking whether auto-merge is enabled for this stack.'
            : ''}
      </span>
    </Dialog>
  )
}

/**
 * The dialog's message. The stack's auto-merge state can arrive after the dialog opens and change it, so a change is
 * announced. The message the dialog opens with isn't, since the dialog itself is announced.
 */
function UnstackMessage({message}: {message: string}) {
  const messageRef = useRef<HTMLDivElement>(null)
  const announcedMessageRef = useRef(message)

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- announcing syncs a screen reader, which is outside React
    if (announcedMessageRef.current === message) return
    announcedMessageRef.current = message
    announce(message, {element: messageRef.current ?? undefined})
  }, [message])

  return <div ref={messageRef}>{message}</div>
}

/** An error appears after confirming, while focus stays on the Unstack button, so each new error is announced. */
function UnstackError({message}: {message: string}) {
  const errorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    announce(message, {element: errorRef.current ?? undefined})
  }, [message])

  return (
    <div ref={errorRef} className="mt-2">
      <InlineMessage variant="critical">{message}</InlineMessage>
    </div>
  )
}
